

import React, { createContext, useState, useEffect, useCallback, useMemo, useContext } from 'react';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Enhanced token validation with admin check
  const validateToken = useCallback(async (token, userData) => {
    try {
      // In a real app, you would verify the token with your backend
      // For demo purposes, we'll just check localStorage consistency
      const storedData = localStorage.getItem('userData');
      if (!storedData) return false;
      
      const parsedData = JSON.parse(storedData);
      
      // Additional check for admin users
      if (parsedData.isAdmin) {
        // Verify admin-specific data exists
        if (!parsedData.mobile || !parsedData.id === 'admin') {
          return false;
        }
      }
      
      return true;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }, []);

  // Initialize auth state with proper admin checks
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = localStorage.getItem('authToken');
        const userData = JSON.parse(localStorage.getItem('userData')) || null;
        
        if (token && userData) {
          const isValid = await validateToken(token, userData);
          
          if (isValid) {
            // Ensure isAdmin is properly set
            const userWithRole = {
              ...userData,
              isAdmin: userData.isAdmin || false
            };
            setUser(userWithRole);
          } else {
            throw new Error('Invalid token or user data');
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear invalid auth data
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [validateToken]);

  // Enhanced login with explicit admin handling
  const login = useCallback(async (userData) => {
    try {
      // Ensure isAdmin is properly set
      const userWithRole = {
        ...userData,
        isAdmin: userData.isAdmin || false,
        // Add token expiration if needed
        expiresAt: userData.expiresAt || Date.now() + 24 * 60 * 60 * 1000 // Default 24h
      };
      
      setUser(userWithRole);
      localStorage.setItem('authToken', userData.token || 'dummy-token');
      localStorage.setItem('userData', JSON.stringify(userWithRole));
      
      return true;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  }, []);

  // Enhanced logout with callback option
  const logout = useCallback((callback) => {
    setUser(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    if (callback && typeof callback === 'function') {
      callback();
    }
  }, []);

  // Session check with admin verification
  const checkSession = useCallback(async () => {
    if (!user) return false;
    
    try {
      const token = localStorage.getItem('authToken');
      const storedData = localStorage.getItem('userData');
      
      if (!token || !storedData) {
        logout();
        return false;
      }

      const isValid = await validateToken(token, JSON.parse(storedData));
      if (!isValid) {
        logout();
        return false;
      }

      // Additional check for admin session
      if (user.isAdmin) {
        const parsedData = JSON.parse(storedData);
        if (!parsedData.isAdmin || parsedData.id !== 'admin') {
          logout();
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Session check error:', error);
      logout();
      return false;
    }
  }, [user, logout, validateToken]);

  // Memoized context value with admin-specific getters
  const contextValue = useMemo(() => ({
    user,
    isLoading,
    login,
    logout,
    checkSession,
    isAuthenticated: !!user,
    isAdmin: user?.isAdmin || false,
    auth: user, // For backward compatibility with your existing code
    // Additional helper methods
    getUserId: () => user?.id || null,
    getUserRole: () => user?.isAdmin ? 'admin' : 'user'
  }), [user, isLoading, login, logout, checkSession]);

  return (
    <AuthContext.Provider value={contextValue}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

// Enhanced useAuth hook with type checking
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};